<script>
	import { onMount } from "svelte";
	import { get } from "svelte/store";
	import { firmwareVersionAll as all } from "../../stores";
	import { putApi as put, delApi as del, postFileApi, putFileApi } from "../../service/api";
	import { Dates } from "../../utils/date";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import { menu, menuSub, menuSubSub } from "../../stores";
	import { ErrorCodes } from "../../define/define";

	// 상수 정의
	const BASE_URL = "/version/firmware";
	const PAGE_SIZES = [10, 15, 20, 25, 30, 50, 100];
	let pageSize = 10; // 페이지당 항목 수
	let totalItems = 0;

	const ACTIVE_OPTIONS = [
		{ value: true, text: "사용" },
		{ value: false, text: "미사용" },
	];

	const FORCE_OPTIONS = [
		{ value: true, text: "필수" },
		{ value: false, text: "선택" },
	];

	// 상태 관리
	let isEditMode = false;
	let editingRowId = null;
	let focusField = "versionCode";
	let filteredData = [];

	// 상태 추가: 저장 중 여부
	let isSaving = false;

	// 페이징 상태
	let currentPage = 1;
	let totalPages = 1;

	// 편집 아이템 초기값
	const defaultEditItem = {
		id: 0,
		versionName: "",
		versionLink: "",
		description: "",
		isForce: false,
		isActive: false,
	};

	let editItem = { ...defaultEditItem };

	// 메서드
	const setFocus = (field = "versionName") => {
		focusField = field;
	};

	let selectedFiles = {
		versionLink: null,
	};

	const fileHandlers = {
		handleFileSelect: async (event) => {
			event.preventDefault();
			const file = event.target.files[0];

			if (!file) return;
			if (!fileHandlers.validateFile(file)) return;

			const reader = new FileReader();
			reader.onload = (e) => {
				selectedFiles.versionLink = file;
				editItem.versionLink = file.name;
			};
			reader.readAsDataURL(file);
		},

		validateFile: (file) => {
			if (file.size > 10 * 1024 * 1024) {
				alert("파일 크기는 10MB를 초과할 수 없습니다.");
				return false;
			}

			console.log(file);

			const validTypes = {
				versionLink: "application/octet-stream",
			};

			if (!file.type.startsWith(validTypes.versionLink)) {
				alert(`bin 파일만 업로드 가능합니다.`);
				return false;
			}

			return true;
		},

		resetFileInput: (input) => {
			input.value = "";
			editItem.versionLink = "";
			selectedFiles.versionLink = null;
		},
	};

	const validateItem = () => {
		if (!editItem.versionName) {
			alert("버전명을 입력하세요.");
			setFocus("versionName");
			return false;
		}

		// 수정모드는 필수 아님
		if (!editItem.id) {
			if (!selectedFiles.versionLink) {
				alert("파일을 선택하세요.");
				setFocus("versionLink");
				return false;
			}
		}

		return true;
	};

	const handleSave = async () => {
		isSaving = true; // 저장 시작 시 상태 업데이트
		try {
			if (!validateItem()) {
				isSaving = false; // 유효성 검사 실패 시 상태 초기화
				return;
			}
			console.log("저장 시작, 현재 editItem:", editItem);
			console.log("선택된 파일:", selectedFiles);

			const payload = {
				...editItem,
				isActive: Boolean(editItem.isActive),
			};

			let result;
			let formData = new FormData();
			formData.append("versionName", editItem.versionName);
			formData.append("versionLink", selectedFiles.versionLink);
			formData.append("description", editItem.description);
			formData.append("isForce", editItem.isForce);
			formData.append("isActive", editItem.isActive);

			if (editItem.id) {
				formData.append("id", editItem.id);
				result = await putFileApi(BASE_URL, formData);
			} else {
				result = await postFileApi(BASE_URL, formData);
			}
			switch (result.resultCode) {
				case ErrorCodes.OK:
					if (editItem.id) {
						alert("펌웨어 버전 수정이 완료되었습니다.");
					} else {
						alert("펌웨어 버전 등록이 완료되었습니다.");
					}
					// 업로드 성공 후 값 초기화
					editItem = { ...defaultEditItem };
					selectedFiles = { versionLink: null };
					isSaving = false; // 저장 완료 후 상태 초기화
					await fnSearch(); // 목록 새로고침
					break;
				default:
					alert(result.resultDescKo);
					break;
			}
		} finally {
			isSaving = false; // 저장 완료 후 상태 초기화
		}
	};

	const fnSearch = async () => {
		await all.fetch(`${BASE_URL}/${pageSize}/${currentPage}`);
		resetEdit();
	};

	const resetEdit = () => {
		isEditMode = false;
		editingRowId = null;
		editItem = { ...defaultEditItem };
	};

	/**
	 * list[0]에 담긴 최신 버전명을 가져와
	 * 마지막 세그먼트만 +1 한 문자열을 반환
	 * ex) "0.0.4.1" → "0.0.4.2"
	 */
	function getNextVersion() {
		const data = get(all).data || {};
		const list = data.list || [];
		const latest = list[0]?.versionName || "0.0.0.0";
		const parts = latest.split(".").map((x) => parseInt(x, 10) || 0);
		while (parts.length < 4) parts.push(0);
		parts[3] += 1;
		return parts.join(".");
	}

	/** 신규 등록 버튼 클릭 시 자동 버전 세팅 */
	function fnProcessRegist() {
		if (!isEditMode) {
			isEditMode = true;
			editingRowId = null;
			editItem = { ...defaultEditItem };
			// 자동으로 마지막 버전 +1
			editItem.versionName = getNextVersion();
			focusFirstInput();
		}
	}

	const focusFirstInput = () => {
		setTimeout(() => {
			const versionNameInput = document.querySelector('input[type="text"]');
			if (versionNameInput) {
				versionNameInput.focus();
				versionNameInput.select();
			}
		});
	};

	const fnProcessEdit = (item) => {
		if (!isEditMode) {
			isEditMode = true;
			editingRowId = item.id;
			editItem = { ...item };
			focusFirstInput();
		}
	};

	const fnDelete = async (id) => {
		const item = $all.data.list.find((item) => item.id === id);
		if (item.isActive) {
			alert("사용 중인 펌웨어 버전은 삭제할 수 없습니다.");
			return;
		}

		if (confirm("정말 삭제하시겠습니까?")) {
			await del(BASE_URL, { id });
			await fnSearch();
		}
	};

	const fnRegistServer = () => {
		if (confirm("서버에 적용하시겠습니까?")) {
			put(`${BASE_URL}/cache`, {}).then((success) => {
				if (success) {
					alert("서버 적용이 완료되었습니다.");
				}
			});
		}
	};

	// 페이지 변경 핸들러
	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fnSearch();
	};

	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value); // pageSize 값 업데이트
		currentPage = 1; // 페이지를 1로 리셋
		fnSearch(); // 목록 새로고침
	};

	let selectedServer = "";
	let s3Server = "";

	$: {
		const data = $all?.data || {};
		filteredData = data.list || [];
		totalPages = Math.ceil((data.total || 0) / pageSize);
		totalItems = data.total || 0;
		selectedServer = localStorage.getItem("selectedServer");
		switch (selectedServer) {
			case "stg-api-backstage.sleepwave.ai":
				s3Server = "https://stg-resource.sleepwave.ai";
				break;
			case "kr-api-backstage.sleepwave.ai":
			case "us-api-backstage.sleepwave.ai":
				s3Server = "https://resource.sleepwave.ai";
				break;
			case "dev-api-backstage.blaupunkt-solum.cn":
				s3Server = "https://dev-resource.blaupunkt-solum.cn";
				break;
			default:
				s3Server = "https://dev-resource.sleepwave.ai";
				break;
		}
	}

	// 파일 다운로드 핸들러 추가
	const downloadHandlers = {
		download: (path) => {
			console.log(path);
			if (!path) {
				alert("다운로드할 파일이 없습니다.");
				return;
			}

			const fileName = path.split("/").pop();

			fetch(path)
				.then((response) => response.blob())
				.then((blob) => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = fileName;
					document.body.appendChild(a);
					a.click();
					window.URL.revokeObjectURL(url);
					document.body.removeChild(a);
				})
				.catch((error) => {
					console.error("다운로드 실패:", error);
					alert("파일 다운로드에 실패했습니다.");
				});
		},
	};

	onMount(() => {
		menu.set("버전 관리");
		menuSub.set("펌웨어 버전");
		menuSubSub.set("");
		fnSearch();
	});
</script>

<div class="card mb-4">
	<div class="table-responsive text-nowrap">
		<TableToolbar
			{totalItems}
			{currentPage}
			{totalPages}
			{pageSize}
			pageSizes={PAGE_SIZES}
			onPageSizeChange={handlePageSizeChange}
			onReload={fnSearch}
			onRegist={fnProcessRegist}
			onRegistServer={fnRegistServer}
		/>
		<div class="fade-container">
			<!-- 메인 테이블 -->
			<table class="table small">
				<thead>
					<tr style="text-align:center">
						<th width="80">No</th>
						<th width="90">버전명</th>
						<th width="510">다운로드 링크</th>
						<th width="*">설명</th>
						<th width="100">필수여부</th>
						<th width="100">사용여부</th>
						<th width="130">등록일시</th>
						<th width="130">수정일시</th>
						<th width="150">관리</th>
					</tr>
				</thead>
				<tbody>
					{#if isEditMode && !editingRowId}
						<tr style="text-align:center">
							<td />
							<td>
								<input
									type="text"
									class="form-control form-control-sm text-center"
									bind:value={editItem.versionName}
									on:focus={() => setFocus("versionName")}
								/>
							</td>
							<td>
								<div class="input-group input-group-sm">
									<span class="input-group-text">/v3/resources/binary/firmware/</span>
									<input
										type="file"
										id="versionLink"
										accept=".bin"
										class="form-control form-control-sm"
										on:change={(e) => fileHandlers.handleFileSelect(e)}
										bind:value={editItem.versionLink}
										on:focus={() => setFocus("versionLink")}
										style="width: 200px;"
									/>
								</div>
							</td>
							<td>
								<input
									type="text"
									class="form-control form-control-sm text-center"
									bind:value={editItem.description}
									on:focus={() => setFocus("description")}
								/>
							</td>
							<td>
								<div class="form-check form-switch d-flex justify-content-center">
									<input class="form-check-input" type="checkbox" id="isForce-new" bind:checked={editItem.isForce} />
								</div>
							</td>
							<td>
								<div class="form-check form-switch d-flex justify-content-center">
									<input class="form-check-input" type="checkbox" id="isActive-new" bind:checked={editItem.isActive} />
								</div>
							</td>
							<td />
							<td />
							<td>
								{#if isSaving}
									<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
									처리 중...
								{:else}
									<button class="btn btn-sm btn-primary me-1" on:click={handleSave}>Save</button>
								{/if}
								<button class="btn btn-sm btn-outline-secondary" on:click={resetEdit}>Cancel</button>
							</td>
						</tr>
					{/if}

					{#each filteredData as item, index}
						{#if editingRowId === item.id}
							<tr style="text-align:center">
								<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
								<td>
									<input
										type="text"
										class="form-control form-control-sm text-center"
										bind:value={editItem.versionName}
										on:focus={() => setFocus("versionName")}
									/>
								</td>
								<td>
									<div class="input-group input-group-sm">
										<span class="input-group-text">/v3/resources/binary/firmware/</span>
										<input
											type="file"
											id="versionLink"
											accept=".bin"
											class="form-control form-control-sm"
											on:change={(e) => fileHandlers.handleFileSelect(e)}
											bind:value={editItem.versionLink}
											on:focus={() => setFocus("versionLink")}
											style="width: 200px;"
										/>
									</div>
								</td>
								<td>
									<input
										type="text"
										class="form-control form-control-sm text-center"
										bind:value={editItem.description}
										on:focus={() => setFocus("description")}
									/>
								</td>
								<td>
									<div class="form-check form-switch d-flex justify-content-center">
										<input class="form-check-input" type="checkbox" id="isForce-edit" bind:checked={editItem.isForce} />
									</div>
								</td>
								<td>
									<div class="form-check form-switch d-flex justify-content-center">
										<input class="form-check-input" type="checkbox" id="isActive-edit" bind:checked={editItem.isActive} />
									</div>
								</td>
								<td>{Dates.defaultConvertFull(item.createdAt)}</td>
								<td>{Dates.defaultConvertFull(item.updatedAt)}</td>
								<td>
									<button class="btn btn-sm btn-primary me-1" on:click={handleSave}>Save</button>
									<button class="btn btn-sm btn-outline-secondary" on:click={resetEdit}>Cancel</button>
								</td>
							</tr>
						{:else}
							<tr style="text-align:center">
								<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
								<td>{item.versionName}</td>
								<td>
									<span
										class="me-2 text-primary cursor-pointer text-truncate"
										on:click={() => downloadHandlers.download(`${s3Server}/v3/resources/firmware/${item.versionLink}`)}
									>
										{s3Server}/v3/resources/firmware/{item.versionLink}
									</span>
								</td>
								<td>{item.description}</td>
								<td>
									<div class="form-check form-switch d-flex justify-content-center">
										<input class="form-check-input" type="checkbox" checked={item.isForce} disabled readonly />
									</div>
								</td>
								<td>
									<div class="form-check form-switch d-flex justify-content-center">
										<input class="form-check-input" type="checkbox" checked={item.isActive} disabled readonly />
									</div>
								</td>
								<td>{Dates.defaultConvertFull(item.createdAt)}</td>
								<td>{Dates.defaultConvertFull(item.updatedAt)}</td>
								<td>
									<button class="btn btn-sm btn-outline-primary me-1" on:click={() => fnProcessEdit(item)}>Edit</button>
									<button class="btn btn-sm btn-outline-primary" on:click={() => fnDelete(item.id)}>Delete</button>
								</td>
							</tr>
						{/if}
					{/each}
				</tbody>
			</table>
		</div>
		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
</div>
